<template>
  <div>
    <v-card class="rounded-lg shadow-sm">
      <v-card-title class="d-flex align-center justify-space-between py-3 px-4">
        <div class="d-flex align-center">
          <v-icon color="primary" class="me-2">mdi-bus-multiple</v-icon>
          <span class="text-h6 font-weight-bold text-dark-primary">{{ $t("Buses") }}</span>
        </div>
        <div class="d-flex align-center flex-wrap">
          <v-btn color="secondary" class="mx-1" small @click="showVehicleTypesDialog">
            <v-icon left small>mdi-format-list-bulleted-type</v-icon>
            {{ $t('vehicle_types') }}
          </v-btn>
          <create-button :text="$t('add_new_transport_device')" @create="showBusDialog"></create-button>
          <activation-tool-tip model="buses"></activation-tool-tip>
        </div>
      </v-card-title>

      <!-- Server-Side Filter Bar -->
      <div class="px-4 py-2 bg-light border-top border-bottom">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="search"
              :label="$t('search') + ' (نوع, اسم, سنة, لوحة, سائق)...'"
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              hide-details
              clearable
              @input="onFilterChange"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="assignmentStatus"
              :items="assignmentStatusOptions"
              label="حالة التخصيص"
              prepend-inner-icon="mdi-account-switch"
              dense
              outlined
              hide-details
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="licenseStatus"
              :items="licenseStatusOptions"
              label="حالة الرخصة"
              prepend-inner-icon="mdi-file-certificate"
              dense
              outlined
              hide-details
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="12" md="3" class="d-flex align-center justify-end">
            <v-btn
              :color="sortBy === 'expiry_asc' ? 'primary' : 'grey lighten-2'"
              :class="sortBy === 'expiry_asc' ? 'white--text' : 'black--text'"
              small
              depressed
              class="w-100 font-weight-bold"
              @click="toggleSmartExpirySort"
            >
              <v-icon left small>mdi-sort-clock-ascending</v-icon>

الأقرب انتهاءً
              <v-icon right x-small v-if="sortBy === 'expiry_asc'">mdi-check</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="buses"
        :footer-props="footerProps"
      >
        <template v-slot:item.vehicle_type="{ item }">
          {{ getVehicleTypeName(item.vehicle_type) }}
        </template>

        <template v-slot:item.license_expiry="{ item }">
          <div class="d-flex flex-column align-start py-1">
            <v-chip
              v-if="item.license_status_info"
              :color="item.license_status_info.color"
              small
              class="font-weight-bold mb-1"
              :class="{ 'white--text': item.license_status_info.color !== 'grey' && item.license_status_info.color !== 'amber' }"
            >
              {{ item.license_status_info.label }}
            </v-chip>
            <small v-if="item.license_expiry_date" class="text-muted">
              {{ item.license_expiry_date }}
            </small>
            <small v-else class="text-muted">غير مسجل</small>
          </div>
        </template>

        <template v-slot:item.driver="{ item }">
          <div class="d-flex align-center py-1">
            <template v-if="item.driver">
              <v-chip color="success" outlined small class="driver-assigned-chip font-weight-bold me-2" @click="assignDriver(item)">
                <v-icon left x-small>mdi-account-check</v-icon>
                {{ item.driver.name || 'سائق' }}
              </v-chip>
              <span class="text-caption text-muted">
                ({{ item.driver.phone_number || item.driver.tel_number || '-' }})
              </span>
            </template>
            <template v-else>
              <v-chip color="grey lighten-2" class="grey--text text--darken-2 font-weight-medium" small @click="assignDriver(item)">
                <v-icon left x-small>mdi-account-plus</v-icon>
                لا يوجد سائق
              </v-chip>
            </template>
          </div>
        </template>

        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small color="primary" class="mx-1" @click="openVehicleDetails(item)">
                mdi-information-outline
              </v-icon>
            </template>
            <span>تفاصيل السيارة وسجل التخصيص</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-if="item.driver" v-bind="attrs" v-on="on" small class="mx-1" @click="unAssignDriver(item)">
                mdi-account-off
              </v-icon>
              <v-icon v-else v-bind="attrs" v-on="on" small class="mx-1" @click="assignDriver(item)">
                mdi-account-tie-hat
              </v-icon>
            </template>
            <span>{{ item.driver ? 'إلغاء تخصيص الكابتن' : 'تخصيص كابتن' }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="mx-1" @click="editBus(item)">
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small color="error" class="mx-1" @click="deleteBus(item, buses.indexOf(item))">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create / Edit Bus Modal -->
    <v-row justify="center">
      <v-dialog
        v-model="busDialog"
        persistent
        max-width="900px"
      >
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation>
          <v-card class="rounded-lg">
            <v-card-title class="primary white--text py-3 px-4">
              <span class="text-h6 font-weight-bold">
                {{ id ? $t("edit_bus") : $t("Bus data") }}
              </span>
            </v-card-title>
            <v-card-text class="pt-4">
              <v-container>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <v-text-field
                      v-model="license"
                      :rules="licenseRules"
                      :label="$t('license_plate') + '*'"
                      :hint="$t('license_plate_hint')"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-select
                      v-model="vehicle_type_id"
                      :items="activeVehicleTypes"
                      item-text="name_display"
                      item-value="id"
                      :label="$t('vehicle_type')"
                      clearable
                      @change="onVehicleTypeChange"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-text-field
                      v-model.number="capacity"
                      :rules="capacityRules"
                      :label="$t('capacity') + '*'"
                      :hint="$t('capacity_hint')"
                      type="number"
                      required
                      @input="onCapacityInput"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-text-field
                      v-model="price_factor"
                      :rules="priceFactorRules"
                      :label="$t('price_factor') + '*'"
                      :hint="$t('pricing_factor_hint')"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field
                      v-model="license_expiry_date"
                      label="تاريخ انتهاء رخصة السيارة"
                      type="date"
                      hint="رخصة ترخيص المركبة"
                      persistent-hint
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>
                <bus-seat-configurator class="my-6" @config-change="updateSeats" :initial-config="seatConfig"></bus-seat-configurator>
              </v-container>
            </v-card-text>
            <v-card-actions class="pa-4 bg-light">
              <v-spacer></v-spacer>
              <v-btn color="grey darken-1" text @click="busDialog = false">
                {{$t('cancel')}}
              </v-btn>
              <v-btn color="primary" @click="createBus">
                {{$t('Save Changes')}}
              </v-btn>
            </v-card-actions>
          </card>
        </v-form>
      </v-dialog>
    </v-row>

    <!-- Redesigned Driver Selection Modal -->
    <v-dialog v-if="selectedBus" v-model="driversDialog" max-width="560" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="headline primary white--text pa-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="white" class="me-2">mdi-account-tie-hat</v-icon>
            <span class="text-h6 font-weight-bold">اختيار السائق</span>
          </div>
          <v-btn icon color="white" @click="closeDriverDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <div class="px-4 pt-3 pb-2 bg-light border-bottom d-flex align-center justify-space-between">
          <span class="text-subtitle-2 font-weight-bold text-muted">
            <v-icon size="18" color="primary" class="me-1">mdi-bus</v-icon>
            الباص: <strong class="black--text">{{ selectedBus.license }}</strong>
          </span>
          <v-chip x-small color="info" outlined>السعة: {{ selectedBus.capacity }} مقعد</v-chip>
        </div>

        <v-card-text class="pa-4">
          <v-text-field
            v-model="driverSearch"
            label="ابحث باسم السائق أو رقم الهاتف..."
            prepend-inner-icon="mdi-magnify"
            clearable
            outlined
            dense
            hide-details
            class="mb-3"
          ></v-text-field>

          <div v-if="loadingDrivers" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
            <div class="mt-2 text-caption text-muted">جاري تحميل قائمة السائقين...</div>
          </div>

          <div v-else-if="hasDriverError" class="text-center py-6 border rounded-lg bg-light">
            <v-icon size="40" color="error">mdi-alert-circle-outline</v-icon>
            <div class="mt-2 text-subtitle-2 error--text">تعذر تحميل السائقين</div>
            <v-btn color="primary" x-small class="mt-3" @click="loadAvailableDrivers">
              <v-icon left x-small>mdi-refresh</v-icon>
              إعادة المحاولة
            </v-btn>
          </div>

          <div v-else-if="filteredAvailableDrivers.length === 0" class="text-center py-8 border rounded-lg bg-light">
            <v-icon size="48" color="grey lighten-1">mdi-account-search-outline</v-icon>
            <div class="mt-2 text-subtitle-2 text-muted">لا يوجد سائق مطابق لبحثك</div>
          </div>

          <div v-else class="driver-scroll-list pr-1">
            <v-card
              v-for="driver in filteredAvailableDrivers"
              :key="driver.id"
              outlined
              class="mb-2 driver-item-card transition-all"
              :class="{ 'driver-selected-card': isCurrentDriver(driver) }"
              @click="assignDriverToBus(driver)"
            >
              <div class="pa-3 d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-avatar size="42" class="me-3 primary white--text font-weight-bold elevation-1">
                    <v-img
                      v-if="getDriverAvatar(driver) && !failedDriverAvatars[driver.id]"
                      :src="getDriverAvatar(driver)"
                      @error="$set(failedDriverAvatars, driver.id, true)"
                    ></v-img>
                    <span v-else>{{ getDriverInitials(driver.name) }}</span>
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold text-subtitle-2 text-navy d-flex align-center">
                      {{ driver.name }}
                      <v-chip
                        v-if="isCurrentDriver(driver)"
                        color="primary"
                        x-small
                        class="ms-2 font-weight-bold"
                      >
                        مُسند حالياً
                      </v-chip>
                    </div>
                    <div class="text-caption text-muted d-flex align-center mt-1">
                      <v-icon size="14" color="grey" class="me-1">mdi-phone</v-icon>
                      <span>{{ getDriverPhone(driver) }}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <v-icon
                    :color="isCurrentDriver(driver) ? 'primary' : 'grey lighten-1'"
                    size="24"
                  >
                    {{ isCurrentDriver(driver) ? 'mdi-check-circle' : 'mdi-checkbox-blank-circle-outline' }}
                  </v-icon>
                </div>
              </div>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDriverDialog">
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- VEHICLE DETAILS & DRIVER ASSIGNMENT HISTORY MODAL -->
    <v-dialog v-model="detailsDialog" max-width="850" persistent scrollable>
      <v-card class="rounded-lg">
        <!-- Header -->
        <v-card-title class="headline primary white--text py-3 px-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="white" class="me-2">mdi-bus-clock</v-icon>
            <span class="text-h6 font-weight-bold">تفاصيل السيارة وسجل التخصيص</span>
          </div>
          <div class="d-flex align-center">
            <v-btn
              v-if="selectedVehicleDetails && selectedVehicleDetails.bus"
              color="success"
              small
              class="me-2 font-weight-bold"
              @click="exportCsv(selectedVehicleDetails.bus.id)"
            >
              <v-icon left small>mdi-file-excel</v-icon>
              تصدير CSV
            </v-btn>
            <v-btn icon color="white" @click="detailsDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Loading State -->
          <div v-if="detailsLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
            <div class="mt-2 text-caption text-muted">جاري تحميل تفاصيل السيارة وسجل التخصيص...</div>
          </div>

          <!-- Content State -->
          <div v-else-if="selectedVehicleDetails && selectedVehicleDetails.bus">
            <!-- VEHICLE SUMMARY CARDS -->
            <v-row dense class="mb-4">
              <v-col cols="12" sm="6" md="3">
                <v-card outlined class="pa-3 rounded-lg bg-light text-center">
                  <div class="text-caption text-muted mb-1">رقم اللوحة / رخصة المركبة</div>
                  <div class="text-h6 font-weight-bold primary--text">
                    {{ selectedVehicleDetails.bus.license }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined class="pa-3 rounded-lg bg-light text-center">
                  <div class="text-caption text-muted mb-1">نوع السيارة</div>
                  <div class="text-body-1 font-weight-bold text-dark-primary">
                    {{ getVehicleTypeName(selectedVehicleDetails.bus.vehicle_type) }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined class="pa-3 rounded-lg bg-light text-center">
                  <div class="text-caption text-muted mb-1">تاريخ انتهاء الرخصة</div>
                  <div class="text-body-2 font-weight-bold text-dark-primary">
                    {{ selectedVehicleDetails.bus.license_expiry_date || 'غير مسجل' }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined class="pa-3 rounded-lg bg-light text-center">
                  <div class="text-caption text-muted mb-1">حالة الرخصة</div>
                  <v-chip
                    v-if="selectedVehicleDetails.license_status_info"
                    :color="selectedVehicleDetails.license_status_info.color"
                    small
                    class="font-weight-bold"
                    :class="{ 'white--text': selectedVehicleDetails.license_status_info.color !== 'grey' && selectedVehicleDetails.license_status_info.color !== 'amber' }"
                  >
                    {{ selectedVehicleDetails.license_status_info.label }}
                  </v-chip>
                </v-card>
              </v-col>
            </v-row>

            <!-- CURRENT CAPTAIN CARD -->
            <v-card outlined class="pa-4 mb-4 rounded-lg border-primary">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="text-subtitle-1 font-weight-bold d-flex align-center">
                  <v-icon color="primary" small class="me-2">mdi-account-tie-hat</v-icon>
                  الكابتن المخصص حاليًا
                </div>
                <v-chip
                  v-if="selectedVehicleDetails.current_captain"
                  color="success"
                  small
                  class="font-weight-bold"
                >
                  تخصيص حالي
                </v-chip>
              </div>

              <div v-if="selectedVehicleDetails.current_captain" class="d-flex align-center justify-space-between flex-wrap">
                <div class="d-flex align-center">
                  <v-avatar size="48" class="me-3 primary white--text font-weight-bold elevation-1">
                    <v-img
                      v-if="selectedVehicleDetails.current_captain.driver_avatar"
                      :src="selectedVehicleDetails.current_captain.driver_avatar"
                    ></v-img>
                    <span v-else>{{ getDriverInitials(selectedVehicleDetails.current_captain.driver_name) }}</span>
                  </v-avatar>
                  <div>
                    <div class="text-h6 font-weight-bold text-dark-primary">
                      {{ selectedVehicleDetails.current_captain.driver_name }}
                    </div>
                    <div class="text-caption text-muted">
                      <v-icon size="14" class="me-1">mdi-phone</v-icon>
                      {{ selectedVehicleDetails.current_captain.driver_phone }}
                    </div>
                  </div>
                </div>

                <div class="mt-2 mt-sm-0 text-right">
                  <div class="text-caption text-muted">بداية التخصيص</div>
                  <div class="text-body-2 font-weight-bold">
                    {{ selectedVehicleDetails.current_captain.started_at || selectedVehicleDetails.current_captain.started_at_display }}
                  </div>
                  <div class="text-caption text-muted mt-1">
                    المدة: <strong class="primary--text">{{ selectedVehicleDetails.current_captain.duration_human }}</strong>
                  </div>
                </div>
              </div>

              <div v-else class="text-center py-4 bg-light rounded-lg">
                <v-icon size="36" color="grey lighten-1">mdi-account-off-outline</v-icon>
                <div class="text-subtitle-2 text-muted mt-1">لا يوجد كابتن مخصص حاليًا</div>
              </div>
            </v-card>

            <!-- DRIVER ASSIGNMENT HISTORY TIMELINE TABLE -->
            <v-card outlined class="rounded-lg">
              <v-card-title class="text-subtitle-1 font-weight-bold py-3 px-4 bg-light border-bottom">
                <v-icon color="primary" small class="me-2">mdi-history</v-icon>
                سجل تخصيص الكباتن
              </v-card-title>

              <v-data-table
                :headers="historyHeaders"
                :items="selectedVehicleDetails.assignment_history"
                hide-default-footer
                class="elevation-0"
              >
                <template v-slot:item.driver="{ item }">
                  <div class="d-flex align-center py-1">
                    <v-avatar size="32" class="me-2 primary white--text font-weight-bold text-caption">
                      <v-img v-if="item.driver_avatar" :src="item.driver_avatar"></v-img>
                      <span v-else>{{ getDriverInitials(item.driver_name) }}</span>
                    </v-avatar>
                    <div>
                      <div class="font-weight-bold text-dark-primary">{{ item.driver_name }}</div>
                      <div class="text-caption text-muted">{{ item.driver_phone }}</div>
                    </div>
                  </div>
                </template>

                <template v-slot:item.started_at="{ item }">
                  {{ item.started_at || item.started_at_display }}
                </template>

                <template v-slot:item.ended_at="{ item }">
                  <span v-if="item.is_current" class="success--text font-weight-bold">
                    مستمر حتى الآن
                  </span>
                  <span v-else>{{ item.ended_at_display }}</span>
                </template>

                <template v-slot:item.duration_human="{ item }">
                  <v-chip x-small color="grey lighten-3" class="text-dark-primary font-weight-medium">
                    {{ item.duration_human }}
                  </v-chip>
                </template>

                <template v-slot:item.status_label="{ item }">
                  <v-chip
                    :color="item.is_current ? 'success' : 'grey'"
                    small
                    class="font-weight-bold white--text"
                  >
                    {{ item.status_label }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="detailsDialog = false">
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vehicle Types Dialog -->
    <v-dialog v-model="vehicleTypesDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ $t("vehicle_types") }}</span>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showAddTypeForm = true; editingType = null; resetTypeForm()">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("add_vehicle_type") }}
          </v-btn>
          <v-btn icon @click="vehicleTypesDialog = false" class="mx-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-expand-transition>
            <div v-show="showAddTypeForm || editingType">
              <v-card outlined class="pa-4 my-4">
                <v-card-title class="text-subtitle-1 py-0">
                  {{ editingType ? $t("edit_vehicle_type") : $t("add_vehicle_type") }}
                </v-card-title>
                <v-form ref="typeForm" v-model="typeFormValid" lazy-validation>
                  <v-row>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="typeForm.name_ar"
                        :rules="[v => !!v || $t('name_ar') + ' ' + $t('is_required')]"
                        :label="$t('name_ar') + '*'"
                        required
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="typeForm.name_en"
                        :label="$t('name_en')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                      <v-text-field
                        v-model.number="typeForm.seats_count"
                        type="number"
                        :rules="[
                          v => !!v || $t('seats_count') + ' ' + $t('is_required'),
                          v => v > 0 || $t('seats_count') + ' ' + $t('must_be_greater_than_0')
                        ]"
                        :label="$t('seats_count') + '*'"
                        required
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                      <v-text-field
                        v-model.number="typeForm.sort_order"
                        type="number"
                        :rules="[v => v >= 0 || $t('sort_order') + ' ' + $t('is_not_valid')]"
                        :label="$t('sort_order')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="d-flex align-center">
                      <v-checkbox
                        v-model="typeForm.is_active"
                        :label="$t('is_active')"
                      ></v-checkbox>
                    </v-col>
                  </row>
                  <v-row justify="end" class="mt-2">
                    <v-btn color="error" text class="mr-2" @click="cancelTypeForm">
                      {{ $t("cancel") }}
                    </v-btn>
                    <v-btn color="success" @click="saveVehicleType">
                      {{ $t("Save Changes") }}
                    </v-btn>
                  </v-row>
                </v-form>
              </v-card>
            </div>
          </v-expand-transition>

          <v-data-table
            :headers="translatedTypeHeaders"
            :items="vehicleTypes"
            :loading="loadingTypes"
            :no-data-text="$t('noData')"
            :no-results-text="$t('noResults')"
            :loading-text="$t('loading_text')"
            class="elevation-1 mt-4"
          >
            <template v-slot:item.name_ar="{ item }">
              {{ item.name_ar }}
            </template>
            <template v-slot:item.name_en="{ item }">
              {{ item.name_en || '-' }}
            </template>
            <template v-slot:item.is_active="{ item }">
              <v-chip :color="item.is_active ? 'success' : 'error'" dark small>
                {{ item.is_active ? $t('active') : $t('inactive') }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon small class="mx-2" @click="editVehicleType(item)">
                mdi-pencil
              </v-icon>
              <v-icon small class="mx-2" color="error" @click="deleteVehicleType(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="vehicleTypesDialog = false">
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ActivationToolTip from "@/components/ActivationToolTip";
import CreateButton from "@/components/CreateButton";
import BusSeatConfigurator from './BusSeatConfigurator.vue';

export default {
  components: {
    ActivationToolTip,
    CreateButton,
    BusSeatConfigurator,
  },
  data() {
    return {
      buses: [],
      availableDrivers: [],
      driverSearch: "",
      failedDriverAvatars: {},
      isLoading: false,
      hasBusesError: false,
      hasDriverError: false,
      search: "",
      assignmentStatus: "all",
      licenseStatus: "all",
      sortBy: "",
      busDialog: false,
      driversDialog: false,
      loadingDrivers: false,
      selectedBus: null,

      // Vehicle details modal state
      detailsDialog: false,
      detailsLoading: false,
      selectedVehicleDetails: null,

      valid: true,
      id: null,
      license: "",
      capacity: 20,
      license_expiry_date: null,
      licenseRules: [
        v => !!v || this.$t('license_plate') + ' ' + this.$t('is_required'),
      ],
      capacityRules: [
        v => !!v || this.$t('capacity') + ' ' + this.$t('is_required'),
        v => v > 0 || this.$t('capacity') + ' ' + this.$t('must_be_greater_than_0'),
        v => /^[0-9]+$/.test(v) || this.$t('capacity') + ' ' + this.$t('is_not_valid'),
      ],
      priceFactor: '1',
      priceFactorRules: [
        v => /^[0-9]+(\.[0-9]+)?$/.test(v) || this.$t('price_factor') + ' ' + this.$t('is_not_valid'),
        v => v > 0 || this.$t('price_factor') + ' ' + this.$t('must_be_greater_than_0'),
      ],
      headers: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "license", value: "license" },
        { text: "vehicle_type", value: "vehicle_type" },
        { text: "capacity", value: "capacity" },
        { text: "رخصة السيارة", value: "license_expiry" },
        { text: "Driver", value: "driver" },
        { text: "created_at", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      historyHeaders: [
        { text: "الكابتن", value: "driver" },
        { text: "تاريخ بداية التخصيص", value: "started_at" },
        { text: "تاريخ نهاية التخصيص", value: "ended_at" },
        { text: "مدة الاستخدام", value: "duration_human" },
        { text: "حالة التخصيص", value: "status_label" },
      ],
      assignmentStatusOptions: [
        { text: "الكل", value: "all" },
        { text: "مخصصة حاليًا", value: "assigned" },
        { text: "غير مخصصة", value: "unassigned" },
      ],
      licenseStatusOptions: [
        { text: "الكل", value: "all" },
        { text: "منتهية", value: "expired" },
        { text: "تنتهي خلال 7 أيام", value: "7_days" },
        { text: "تنتهي خلال 30 يومًا", value: "30_days" },
        { text: "تنتهي خلال 60 يومًا", value: "60_days" },
        { text: "سارية", value: "valid" },
        { text: "غير مسجل", value: "unrecorded" },
      ],
      seatConfig: {
        rows: 5,
        columns: 4,
        seatGrid: [
          [true, true, true, true],
          [true, true, true, true],
          [true, true, true, true],
          [true, true, true, true],
          [true, true, true, true],
        ],
      },
      vehicle_type_id: null,
      previousVehicleTypeId: null,
      activeVehicleTypes: [],
      vehicleTypesDialog: false,
      vehicleTypes: [],
      loadingTypes: false,
      showAddTypeForm: false,
      editingType: null,
      typeFormValid: true,
      typeForm: {
        name_ar: '',
        name_en: '',
        seats_count: 20,
        sort_order: 0,
        is_active: true
      },
      typeHeaders: [
        { text: "name_ar", value: "name_ar" },
        { text: "name_en", value: "name_en" },
        { text: "seats_count", value: "seats_count" },
        { text: "is_active", value: "is_active" },
        { text: "sort_order", value: "sort_order" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    filteredAvailableDrivers() {
      let list = Array.isArray(this.availableDrivers) ? [...this.availableDrivers] : [];

      if (this.selectedBus && this.selectedBus.driver) {
        const exists = list.some(d => d && d.id === this.selectedBus.driver.id);
        if (!exists) {
          list.unshift(this.selectedBus.driver);
        }
      }

      if (!this.driverSearch || !this.driverSearch.trim()) {
        return list;
      }

      const q = this.driverSearch.trim().toLowerCase();
      return list.filter(driver => {
        if (!driver) return false;
        const name = (driver.name || "").toLowerCase();
        const phone = (driver.phone_number || driver.tel_number || (driver.driver_information && driver.driver_information.phone_number) || "").toLowerCase();
        return name.includes(q) || phone.includes(q);
      });
    },
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: header.text.startsWith('license') || header.text.startsWith('vehicle') || header.text === 'id' || header.text === 'capacity' || header.text === 'Driver' || header.text === 'created_at' || header.text === 'Actions'
          ? (this.$t(header.text) || header.text)
          : header.text
      }));
    },
    translatedTypeHeaders() {
      return this.typeHeaders.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page"),
      }
    }
  },
  mounted() {
    this.loadBuses();
    this.loadActiveVehicleTypes();
  },
  methods: {
    async loadBuses() {
      this.isLoading = true;
      this.hasBusesError = false;
      this.buses = [];
      try {
        const response = await axios.get('/buses/all', {
          params: {
            search: this.search,
            assignment_status: this.assignmentStatus,
            license_status: this.licenseStatus,
            sort_by: this.sortBy,
          }
        });
        const rawData = response && response.data ? response.data : [];
        if (Array.isArray(rawData)) {
          this.buses = rawData;
        } else if (rawData && Array.isArray(rawData.data)) {
          this.buses = rawData.data;
        } else if (rawData && Array.isArray(rawData.buses)) {
          this.buses = rawData.buses;
        } else {
          this.buses = [];
        }
      } catch (error) {
        this.hasBusesError = true;
        console.error("Error retrieving buses:", error);
        this.$notify({
          title: this.$t("Error") || "خطأ",
          text: this.$t("error_retrieving_buses") || "تعذر تحميل وسائل النقل",
          type: 'error'
        });
      } finally {
        this.isLoading = false;
      }
    },

    onFilterChange() {
      this.loadBuses();
    },

    toggleSmartExpirySort() {
      if (this.sortBy === 'expiry_asc') {
        this.sortBy = '';
      } else {
        this.sortBy = 'expiry_asc';
      }
      this.loadBuses();
    },

    async openVehicleDetails(bus) {
      if (!bus || !bus.id) return;
      this.detailsLoading = true;
      this.selectedVehicleDetails = null;
      this.detailsDialog = true;
      try {
        const response = await axios.get(`/buses/${bus.id}/details`);
        const resData = response && response.data ? response.data : null;
        const payload = resData && resData.data ? resData.data : resData;
        this.selectedVehicleDetails = payload;
      } catch (error) {
        console.error("Error loading vehicle details:", error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        } : error);

        const backendMessage = error.response?.data?.error?.[0]
          || error.response?.data?.message
          || (error.response ? `HTTP ${error.response.status}: تعذر تحميل البيانات` : "تعذر الاتصال بالخادم");

        this.$notify({
          title: "خطأ في تحميل تفاصيل المركبة",
          text: backendMessage,
          type: "error"
        });
      } finally {
        this.detailsLoading = false;
      }
    },

    async exportCsv(busId) {
      if (!busId) return;
      try {
        const response = await axios.get(`/buses/${busId}/assignment-history/export-csv`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `vehicle_history_${busId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting CSV:", error.response ? {
          status: error.response.status,
          data: error.response.data
        } : error);
        this.$notify({
          title: "خطأ",
          text: "تعذر تصدير ملف CSV",
          type: "error"
        });
      }
    },

    validate() {
      return this.$refs.form.validate();
    },

    createBus() {
      if(this.validate()) {
        this.isLoading = true;
        this.busDialog = false;
        axios
          .post(`/buses/create-edit`, {
            bus: {
              id: this.id,
              license: this.license,
              capacity: this.capacity,
              price_factor: this.priceFactor,
              seat_config: JSON.stringify(this.seatConfig),
              vehicle_type_id: this.vehicle_type_id,
              license_expiry_date: this.license_expiry_date,
            },
          })
          .then((response) => {
            this.loadBuses();
            this.$notify({
              title: this.$t("Success"),
              text: this.id ? this.$t("bus_updated") : this.$t("bus_created"),
              type: 'success'
            });
            this.$swal(this.$t("Success"), this.id ? this.$t("bus_updated_successfully") : this.$t("bus_created_successfully"), "success");
          })
          .catch((error) => {
            this.$notify({
              title: this.$t("Error"),
              text: this.$t("error_creating_bus"),
              type: 'error'
            });
            console.log(error);
            this.$swal("Error", error.response?.data?.message || "Error", "error");
          })
          .then(() => {
            this.isLoading = false;
          });
      }
    },

    showBusDialog() {
      this.license = '';
      this.priceFactor = '1';
      this.id = null;
      this.capacity = 20;
      this.vehicle_type_id = null;
      this.previousVehicleTypeId = null;
      this.license_expiry_date = null;
      this.seatConfig = {
        rows: 5,
        columns: 4,
        seatGrid: [
          [true, true, true, true],
          [true, true, true, true],
          [true, true, true, true],
          [true, true, true, true],
          [true, true, true, true],
        ],
      };
      this.busDialog = true;
    },

    editBus(bus) {
      this.id = bus.id;
      this.license = bus.license;
      this.capacity = bus.capacity;
      this.priceFactor = bus.price_factor || '1';
      this.vehicle_type_id = bus.vehicle_type_id;
      this.previousVehicleTypeId = bus.vehicle_type_id;
      this.license_expiry_date = bus.license_expiry_date || null;
      this.seatConfig = typeof bus.seat_config === 'string' ? JSON.parse(bus.seat_config) : bus.seat_config;
      this.busDialog = true;
    },

    deleteBus(bus, index) {
      this.$swal
        .fire({
          title: this.$t("delete_bus"),
          text: this.$t("confirm_delete_bus", { license: bus.license }),
          icon: "error",
          showCancelButton: true,
          confirmButtonText: this.$t("yes_delete"),
          cancelButtonText: this.$t("cancel"),
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deleteBusServer(bus.id, index);
          }
        });
    },

    deleteBusServer(bus_id, index) {
      axios
        .delete(`/buses/${bus_id}`)
        .then((response) => {
          this.buses.splice(index, 1);
          this.$notify({
            title: this.$t("Success"),
            text: this.$t("bus_deleted"),
            type: "success",
          });
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: this.$t("error_deleting_buses"),
            type: 'error'
          });
          this.$swal("Error", error.response?.data?.message || "Error", "error");
        });
    },

    assignDriver(bus) {
      this.selectedBus = bus;
      this.driverSearch = "";
      this.driversDialog = true;
      this.loadAvailableDrivers();
    },

    closeDriverDialog() {
      this.driversDialog = false;
      this.selectedBus = null;
    },

    async loadAvailableDrivers() {
      this.loadingDrivers = true;
      this.hasDriverError = false;
      this.availableDrivers = [];
      try {
        const response = await axios.get('/buses/available-drivers', {
          params: { bus_id: this.selectedBus ? this.selectedBus.id : null }
        });
        this.availableDrivers = response.data || [];
      } catch (error) {
        this.hasDriverError = true;
        console.error("Error loading available drivers:", error);
      } finally {
        this.loadingDrivers = false;
      }
    },

    isCurrentDriver(driver) {
      if (!this.selectedBus || !this.selectedBus.driver || !driver) return false;
      return (this.selectedBus.driver.id === driver.id);
    },

    async assignDriverToBus(driver) {
      if (!this.selectedBus || !driver) return;

      if (this.isCurrentDriver(driver)) {
        this.closeDriverDialog();
        return;
      }

      this.isLoading = true;
      const targetBus = this.selectedBus;
      this.closeDriverDialog();

      try {
        await axios.post('/buses/assign-driver', {
          bus_id: targetBus.id,
          driver_id: driver.id
        });
        this.$notify({
          title: this.$t("Success"),
          text: "تم تخصيص الكابتن بنجاح",
          type: "success"
        });
        this.loadBuses();
      } catch (error) {
        console.error("Error assigning driver:", error);
        this.$notify({
          title: this.$t("Error"),
          text: error.response?.data?.error?.[0] || error.response?.data?.message || "تعذر تخصيص الكابتن",
          type: "error"
        });
      } finally {
        this.isLoading = false;
      }
    },

    async unAssignDriver(bus) {
      this.$swal.fire({
        title: "إلغاء تخصيص الكابتن",
        text: `هل أنت تأكد من إلغاء تخصيص الكابتن من السيارة (لوحة ${bus.license})؟`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "نعم، إلغاء التخصيص",
        cancelButtonText: this.$t("cancel"),
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.isLoading = true;
          try {
            await axios.post('/buses/unassign-driver', {
              bus_id: bus.id
            });
            this.$notify({
              title: this.$t("Success"),
              text: "تم إلغاء تخصيص الكابتن بنجاح",
              type: "success"
            });
            this.loadBuses();
          } catch (error) {
            console.error("Error unassigning driver:", error);
            this.$notify({
              title: this.$t("Error"),
              text: error.response?.data?.error?.[0] || error.response?.data?.message || "تعذر إلغاء تخصيص الكابتن",
              type: "error"
            });
          } finally {
            this.isLoading = false;
          }
        }
      });
    },

    getDriverAvatar(driver) {
      if (!driver) return null;
      if (driver.photo) return '/storage/' + driver.photo;
      if (driver.avatar) return driver.avatar;
      return null;
    },

    getDriverInitials(name) {
      if (!name) return 'D';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getDriverPhone(driver) {
      if (!driver) return '-';
      return driver.phone_number || driver.tel_number || (driver.driver_information && driver.driver_information.phone_number) || '-';
    },

    getVehicleTypeName(vt) {
      if (!vt) return '-';
      if (typeof vt === 'string') return vt;
      return vt.name_ar || vt.name_en || '-';
    },

    updateSeats(config) {
      this.seatConfig = config;
    },

    showVehicleTypesDialog() {
      this.vehicleTypesDialog = true;
      this.loadVehicleTypes();
    },

    async loadVehicleTypes() {
      this.loadingTypes = true;
      try {
        const response = await axios.get('/vehicle-types');
        this.vehicleTypes = response.data || [];
      } catch (error) {
        console.error("Error loading vehicle types:", error);
      } finally {
        this.loadingTypes = false;
      }
    },

    async loadActiveVehicleTypes() {
      try {
        const response = await axios.get('/vehicle-types/active');
        const types = response.data || [];
        this.activeVehicleTypes = types.map(t => ({
          ...t,
          name_display: `${t.name_ar} (${t.seats_count} مقعد)`
        }));
      } catch (error) {
        console.error("Error loading active vehicle types:", error);
      }
    },

    resetTypeForm() {
      this.typeForm = {
        name_ar: '',
        name_en: '',
        seats_count: 20,
        sort_order: 0,
        is_active: true
      };
    },

    cancelTypeForm() {
      this.showAddTypeForm = false;
      this.editingType = null;
      this.resetTypeForm();
    },

    editVehicleType(type) {
      this.editingType = type;
      this.typeForm = {
        name_ar: type.name_ar,
        name_en: type.name_en,
        seats_count: type.seats_count,
        sort_order: type.sort_order || 0,
        is_active: !!type.is_active
      };
      this.showAddTypeForm = true;
    },

    async saveVehicleType() {
      if (!this.$refs.typeForm.validate()) return;

      this.loadingTypes = true;
      try {
        if (this.editingType) {
          await axios.put(`/vehicle-types/${this.editingType.id}`, this.typeForm);
          this.$notify({ title: this.$t("Success"), text: this.$t("vehicle_type_updated_successfully"), type: 'success' });
        } else {
          await axios.post('/vehicle-types', this.typeForm);
          this.$notify({ title: this.$t("Success"), text: this.$t("vehicle_type_created_successfully"), type: 'success' });
        }
        this.cancelTypeForm();
        this.loadVehicleTypes();
        this.loadActiveVehicleTypes();
      } catch (error) {
        this.$notify({ title: this.$t("Error"), text: error.response?.data?.message || this.$t("error_saving_vehicle_type"), type: 'error' });
      } finally {
        this.loadingTypes = false;
      }
    },

    deleteVehicleType(type) {
      this.$swal.fire({
        title: this.$t("delete_vehicle_type"),
        text: this.$t("confirm_delete_vehicle_type", { name: type.name_ar }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yes_delete"),
        cancelButtonText: this.$t("cancel"),
      }).then((result) => {
        if (result.isConfirmed) {
          this.loadingTypes = true;
          axios.delete(`/vehicle-types/${type.id}`)
            .then(() => {
              this.loadVehicleTypes();
              this.loadActiveVehicleTypes();
              this.$notify({ title: this.$t("Success"), text: this.$t("vehicle_type_deleted_successfully"), type: 'success' });
            })
            .catch((error) => {
              this.$notify({ title: this.$t("Error"), text: error.response?.data?.message || this.$t("error_deleting_vehicle_type"), type: 'error' });
            })
            .then(() => {
              this.loadingTypes = false;
            });
        }
      });
    },

    onVehicleTypeChange(typeId) {
      if (!typeId) return;
      const selectedType = this.activeVehicleTypes.find(t => t.id === typeId);
      if (selectedType) {
        if (this.capacity && this.capacity !== selectedType.seats_count) {
          this.$swal.fire({
            title: this.$t("confirm_change_vehicle_type"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: this.$t("yes"),
            cancelButtonText: this.$t("cancel"),
          }).then((result) => {
            if (result.isConfirmed) {
              this.applyVehicleType(selectedType);
            } else {
              this.vehicle_type_id = this.previousVehicleTypeId;
            }
          });
        } else {
          this.applyVehicleType(selectedType);
        }
      }
    },

    applyVehicleType(selectedType) {
      this.vehicle_type_id = selectedType.id;
      this.previousVehicleTypeId = selectedType.id;
      this.capacity = selectedType.seats_count;
      this.seatConfig = this.generateSeatConfig(selectedType.seats_count);
    },

    generateSeatConfig(capacity) {
      const columns = 4;
      const rows = Math.ceil(capacity / columns);
      const seatGrid = [];
      let remainingSeats = capacity;
      for (let i = 0; i < rows; i++) {
        const row = [];
        for (let j = 0; j < columns; j++) {
          if (remainingSeats > 0) {
            row.push(true);
            remainingSeats--;
          } else {
            row.push(false);
          }
        }
        seatGrid.push(row);
      }
      return { rows, columns, seatGrid };
    },

    onCapacityInput(val) {
      const newCapacity = parseInt(val);
      if (!isNaN(newCapacity) && newCapacity > 0) {
        let currentSeatsCount = 0;
        if (this.seatConfig && this.seatConfig.seatGrid) {
          currentSeatsCount = this.seatConfig.seatGrid.reduce((total, row) => total + row.filter(s => s).length, 0);
        }
        if (newCapacity !== currentSeatsCount) {
          this.seatConfig = this.generateSeatConfig(newCapacity);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.driver-item-card {
  cursor: pointer;
  border-radius: 8px !important;
  border: 1px solid #E4E8F0 !important;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3B82F6 !important;
    background-color: #F8FAFC !important;
  }
}

.driver-selected-card {
  border-color: #3B82F6 !important;
  background-color: #EFF6FF !important;
}

.driver-scroll-list {
  max-height: 380px;
  overflow-y: auto;
}

.text-navy {
  color: #082C69 !important;
}

.border-primary {
  border-color: #3B82F6 !important;
}

.driver-assigned-chip {
  ::v-deep .v-chip__content {
    color: #111827 !important;
    opacity: 1 !important;
    font-weight: 700 !important;
  }
}

::v-deep .v-data-table {
  color: #111827 !important;
}
::v-deep .v-data-table thead th {
  color: #111827 !important;
  font-weight: 800 !important;
  font-size: 13px !important;
  background-color: #f8fafc !important;
}
::v-deep .v-data-table tbody td {
  color: #111827 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  opacity: 1 !important;
}
::v-deep .v-data-footer,
::v-deep .v-data-footer *,
::v-deep .v-pagination,
::v-deep .v-pagination * {
  color: #111827 !important;
  opacity: 1 !important;
}
</style>
